import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  id: string;
  /** What the toggle reveals, read by screen readers. Set it when a form has two password fields. */
  revealLabel?: string;
}

/**
 * Password field with an eye toggle. Going back to hidden on submit keeps the
 * browser from remembering the value and leaves nothing on a shared screen.
 */
export default function PasswordInput({ id, revealLabel = 'palavra-passe', ...inputProps }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const form = inputRef.current?.form;
    if (!form) return;
    const hide = () => setVisible(false);
    form.addEventListener('submit', hide);
    return () => form.removeEventListener('submit', hide);
  }, []);

  return (
    <div className="relative mt-1">
      <input
        {...inputProps}
        ref={inputRef}
        id={id}
        type={visible ? 'text' : 'password'}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className="input pr-10"
      />
      <button
        type="button"
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground"
        aria-label={`${visible ? 'Ocultar' : 'Mostrar'} ${revealLabel}`}
        aria-controls={id}
        onClick={() => setVisible((shown) => !shown)}
      >
        {visible ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
      </button>
    </div>
  );
}
